import z from "zod";
import pickBy from "lodash/pickBy";
import identity from "lodash/identity";

import fetcher from "@/lib/fetcher";
import { objToCamelCase } from "@/lib/utils";
import { actionClient } from "@/lib/safe-action";
import handleErrorResponse from "@/lib/handle-error-response";

import ResourceNotFoundError from "@/error/resource-not-found-error";
import ResourseError from "@/error/resourse-error";

/**
 * Get course detail
 * @param {string} slug - The slug of the course
 * @returns {Promise<Object>} The course detail
 */
export async function getCourseDetail(slug, languageId = null) {
  const response = await fetcher("/api/store-ws/api/v1/course/view", {
    params: { courseSlug: slug, languageId },
    next: {
      revalidate: 60 * 5, // Revalidate every 5 minutes
    },
  });

  if (!response.ok) return handleErrorResponse(response);

  const data = await response.json();
  if (data.success) return objToCamelCase(data.data);

  return null;
}

/**
 * Get user course detail
 *
 * @returns {Promise<Object>} The user course detail
 */
export const getUserCourseDetail = actionClient
  .inputSchema(
    z.object({
      courseSlug: z.string().describe("Course Slug"),
      offlineCentreId: z.number().optional().describe("Offline Centre ID"),
    })
  )
  .action(async ({ clientInput }) => {
    const response = await fetcher(
      "/api/app-content-ws/api/user/course/details",
      { params: clientInput }
    );

    if (!response.ok) return handleErrorResponse(response);

    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    return null;
  });

export const getCoursePackages = actionClient
  .inputSchema(
    z.object({
      courseId: z.number().describe("Course Id"),
      languageId: z.number().describe("Language Id"),
      isOfflineCourse: z.number().default(0).describe("Is Course Offline?"),
      offlineCentreId: z.number().optional().describe("Offline Center ID"),
      action: z.number().describe("Purchase Action"),
    })
  )
  .action(async ({ parsedInput }) => {
    const response = await fetcher(
      `/api/store-ws/api/v1/course/package/details`,
      { params: pickBy(parsedInput, identity) }
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new ResourceNotFoundError("No Course Packages Found");
      } else {
        throw new ResourseError("Failed to fetch course packages");
      }
    }

    // Return the data from the response
    const data = await response.json();
    if (data.success) return objToCamelCase(data.data);

    return null;
  });

export const getCourseContent = actionClient
  .inputSchema(
    z.object({
      courseId: z.number(),
      languageId: z.number(),
      offlineCentreId: z.number().optional(),
    })
  )
  .action(
    async ({ clientInput: { courseId, languageId, offlineCentreId } }) => {
      const response = await fetcher("/api/web/course_content", {
        params: {
          course_id: courseId,
          language_id: languageId,
          offlineCentreId,
        },
      });

      if (!response.ok) return handleErrorResponse(response);

      const data = await response.json();
      if (data.success) return objToCamelCase(data.data);

      return null;
    }
  );
